import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { useOrg } from '@/contexts/OrgContext'
import { useCadence } from '@/contexts/CadenceContext'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Zap,
  Loader2,
  Users,
  Brain,
  AlertTriangle,
  Search,
  CheckCircle,
  FlaskConical,
  BookOpen,
  Clock,
  FileText,
  Hand,
  Power,
} from 'lucide-react'
import { STEP_TYPE_CONFIG, type Cadence, type CadenceStep, type Lead } from '@/types'
import type { AIPrompt, ExampleSection } from '@/lib/edge-functions'

// Returns 'YYYY-MM-DD' for a given dayOffset in a timezone (skips weekends).
// CA-049(a): day_offset=0 no longer returns today verbatim — if today is a
// weekend the first touch rolls forward to the next business day, matching the
// business-days policy applied to every other offset (a Saturday launch used to
// schedule the Day-0 step on that Saturday).
function getDefaultDate(dayOffset: number, tz: string, fromDate: Date): string {
  const dateFmt = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })
  const todayStr = dateFmt.format(fromDate)
  const [y, m, d] = todayStr.split('-').map(Number)
  const target = new Date(y, m - 1, d)
  if (dayOffset === 0) {
    // Roll off a weekend to the next business day (Mon).
    while (target.getDay() === 0 || target.getDay() === 6) {
      target.setDate(target.getDate() + 1)
    }
  } else {
    let remaining = dayOffset
    while (remaining > 0) {
      target.setDate(target.getDate() + 1)
      if (target.getDay() !== 0 && target.getDay() !== 6) remaining--
    }
  }
  const y2 = target.getFullYear()
  const m2 = String(target.getMonth() + 1).padStart(2, '0')
  const d2 = String(target.getDate()).padStart(2, '0')
  return `${y2}-${m2}-${d2}`
}

// CA-034(a): derive the send_note map from each step's saved config_json so the
// checkbox and the submit agree with what's persisted.
function seedSendNote(steps: CadenceStep[]): Record<string, boolean> {
  const initial: Record<string, boolean> = {}
  for (const step of steps) {
    if (step.step_type !== 'linkedin_connect') continue
    const saved = (step.config_json as Record<string, unknown> | null)?.send_note
    if (typeof saved === 'boolean') initial[step.id] = saved
  }
  return initial
}

// CA-034(b): derive the signals toggle from the first step that has a persisted
// use_signals, defaulting to true only when nothing was ever saved.
function seedUseSignals(steps: CadenceStep[]): boolean {
  for (const step of steps) {
    const saved = (step.config_json as Record<string, unknown> | null)?.use_signals
    if (typeof saved === 'boolean') return saved
  }
  return true
}

// Per-step default times (HH:MM): saved config_json.scheduled_time wins;
// otherwise the first unsaved step of a day starts at now+5min (rounded to the
// next 2-min boundary) and later steps of that day are staggered +2min.
// CA-049(b): recomputed each time the dialog OPENS (via the effect below) so the
// "now+5min" defaults reflect the current clock instead of the stale value from
// when the page first mounted (which reopened as a likely-past 09:00 fallback).
function seedScheduledTimes(steps: CadenceStep[], from: Date): Record<string, string> {
  const sorted = [...steps].sort((a, b) => a.day_offset - b.day_offset || a.order_in_day - b.order_in_day)
  const times: Record<string, string> = {}
  const dayBaseMinutes: Record<number, number> = {}

  sorted.forEach((step) => {
    const existing = (step.config_json as Record<string, unknown>)?.scheduled_time as string | undefined
    if (existing) {
      times[step.id] = existing
      // Track the running base for this day so subsequent steps are spaced correctly
      const [h, m] = existing.split(':').map(Number)
      dayBaseMinutes[step.day_offset] = (h * 60 + m) + 2
    } else {
      if (dayBaseMinutes[step.day_offset] === undefined) {
        // First unsaved step on this day: default to now+5min rounded to next 2-min boundary
        const rawMin = from.getHours() * 60 + from.getMinutes() + 5
        dayBaseMinutes[step.day_offset] = Math.ceil(rawMin / 2) * 2
      }
      const totalMin = Math.min(dayBaseMinutes[step.day_offset], 23 * 60 + 58)
      const h = Math.floor(totalMin / 60)
      const m = totalMin % 60
      times[step.id] = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
      dayBaseMinutes[step.day_offset] = totalMin + 2
    }
  })
  return times
}

interface StartAutomationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cadence: Cadence
  steps: CadenceStep[]
  aiPrompts: AIPrompt[]
}

export function StartAutomationDialog({
  open,
  onOpenChange,
  cadence,
  steps,
  aiPrompts,
}: StartAutomationDialogProps) {
  const { user } = useAuth()
  const { orgId } = useOrg()
  const { updateCadence } = useCadence()
  const queryClient = useQueryClient()
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [starting, setStarting] = useState(false)
  const [revertingToManual, setRevertingToManual] = useState(false)
  // Per-step overrides: stepId → promptId/sectionId
  const [stepMessageOverrides, setStepMessageOverrides] = useState<Record<string, string>>({})
  const [stepResearchOverrides, setStepResearchOverrides] = useState<Record<string, string>>({})
  const [stepExampleOverrides, setStepExampleOverrides] = useState<Record<string, string>>({})
  const [stepTemplateOverrides, setStepTemplateOverrides] = useState<Record<string, string>>({})
  // LinkedIn Connect: whether to send a note with the connection request.
  // CA-034(a): hydrate from each step's saved config_json.send_note so reopening
  // the dialog reflects the persisted value. Previously this started empty ({}),
  // so a step saved with send_note=true showed an UNCHECKED box while the submit
  // left send_note=true untouched → the connect still went out WITH a note,
  // contradicting what the UI showed (and cadence v2's plain-invite policy).
  const [stepSendNote, setStepSendNote] = useState<Record<string, boolean>>(() => seedSendNote(steps))
  // Per-step scheduled times (HH:MM) and timezone
  const [stepScheduledTimes, setStepScheduledTimes] = useState<Record<string, string>>(() => seedScheduledTimes(steps, new Date()))
  // Per-step scheduled dates (YYYY-MM-DD) — default: today + day_offset business days
  const [stepScheduledDates, setStepScheduledDates] = useState<Record<string, string>>({})
  // CA-034(b): hydrate from saved config instead of hardcoding true. Previously
  // this defaulted to true and was written UNCONDITIONALLY to every step on each
  // submit, so a user who had turned signals OFF saw it flip back to 'Activadas'
  // on reopen and — if they didn't touch the switch — the submit silently
  // overwrote their saved false back to true. Seed from the first step that has
  // a persisted use_signals so the toggle mirrors what's stored.
  const [useSignals, setUseSignals] = useState<boolean>(() => seedUseSignals(steps))
  const [timezone, setTimezone] = useState(cadence.timezone || 'America/New_York')

  // The dialog is kept MOUNTED by its parent (rendered whenever `cadence`
  // exists, not on open), and handleStart resets the config-derived state to
  // empty on success. So the mount-time seeds above go stale after the first
  // run. Re-hydrate on every closed→open transition so send_note / use_signals
  // reflect the freshly-persisted config (CA-034) and the default times are
  // recomputed against the CURRENT clock instead of a likely-past 09:00 fallback
  // (CA-049b). Depends only on `open`: seeding reads the latest `steps`/`cadence`
  // props at run time without re-firing when they change mid-session.
  useEffect(() => {
    if (!open) return
    setStepSendNote(seedSendNote(steps))
    setUseSignals(seedUseSignals(steps))
    setStepScheduledTimes(seedScheduledTimes(steps, new Date()))
    setStepScheduledDates({})
    setStepMessageOverrides({})
    setStepResearchOverrides({})
    setStepExampleOverrides({})
    setStepTemplateOverrides({})
    setTimezone(cadence.timezone || 'America/New_York')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Fetch example sections
  const { data: exampleSections = [] } = useQuery({
    queryKey: ['example-sections', orgId, user?.id],
    queryFn: async () => {
      if (!user?.id) return []
      const { data, error } = await supabase
        .from('example_sections')
        .select('*')
        .eq('org_id', orgId!)
        .eq('owner_id', user.id)
        .order('name', { ascending: true })
      if (error) throw error
      return (data || []) as ExampleSection[]
    },
    enabled: !!user?.id && !!orgId && open,
  })

  // Fetch templates
  const { data: templates = [] } = useQuery({
    queryKey: ['templates-for-automation', orgId, user?.id],
    queryFn: async () => {
      if (!user?.id) return []
      const { data, error } = await supabase
        .from('templates')
        .select('id, name, body_template')
        .eq('org_id', orgId!)
        .eq('owner_id', user.id)
        .order('name', { ascending: true })
      if (error) throw error
      return (data || []) as { id: string; name: string; body_template: string }[]
    },
    enabled: !!user?.id && !!orgId && open,
  })

  // Fetch all leads
  const { data: allLeads = [] } = useQuery({
    queryKey: ['leads-for-automation', orgId, user?.id],
    queryFn: async () => {
      if (!user?.id) return []
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('org_id', orgId!)
        .eq('owner_id', user.id)
        .order('first_name', { ascending: true })
        .limit(10000)
      if (error) throw error
      return (data || []) as Lead[]
    },
    enabled: !!user?.id && !!orgId && open,
  })

  // Fetch leads already in this cadence
  const { data: existingCadenceLeads = [] } = useQuery({
    queryKey: ['cadence-leads-existing', cadence.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('cadence_leads')
        .select('lead_id')
        .eq('cadence_id', cadence.id)
        .in('status', ['active', 'scheduled', 'pending', 'generated'])
        .limit(10000)
      if (error) throw error
      return (data || []).map((cl: { lead_id: string }) => cl.lead_id)
    },
    enabled: open,
  })

  const existingLeadSet = new Set(existingCadenceLeads)

  // Filter leads — show only leads already in this cadence
  const availableLeads = allLeads.filter((lead) => existingLeadSet.has(lead.id))
  const filteredLeads = availableLeads.filter((lead) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      lead.first_name?.toLowerCase().includes(q) ||
      lead.last_name?.toLowerCase().includes(q) ||
      lead.company?.toLowerCase().includes(q) ||
      lead.email?.toLowerCase().includes(q)
    )
  })

  // Steps sorted by day
  const sortedSteps = [...steps].sort((a, b) => a.day_offset - b.day_offset || a.order_in_day - b.order_in_day)

  // Check which steps still need AI prompts (accounting for overrides)
  const stepsWithoutAI = sortedSteps.filter((step) => {
    const config = step.config_json as Record<string, unknown>
    const hasMessageOverride = !!stepMessageOverrides[step.id] && stepMessageOverrides[step.id] !== 'none'
    const hasTemplateOverride = !!stepTemplateOverrides[step.id] && stepTemplateOverrides[step.id] !== 'none'
    // LinkedIn Connect with sendNote off doesn't need a prompt
    if (step.step_type === 'linkedin_connect' && !stepSendNote[step.id]) return false
    return !config?.ai_prompt_id && !hasMessageOverride && !hasTemplateOverride && !config?.message_template && !config?.template_id && !!STEP_TYPE_CONFIG[step.step_type]?.hasTextBox
  })

  // Filtered prompt lists
  const researchPrompts = aiPrompts.filter((p) => p.prompt_type === 'research')

  const toggleLead = (leadId: string) => {
    setSelectedLeadIds((prev) => {
      const next = new Set(prev)
      if (next.has(leadId)) {
        next.delete(leadId)
      } else {
        next.add(leadId)
      }
      return next
    })
  }

  const toggleAll = () => {
    if (selectedLeadIds.size === filteredLeads.length) {
      setSelectedLeadIds(new Set())
    } else {
      setSelectedLeadIds(new Set(filteredLeads.map((l) => l.id)))
    }
  }

  const handleStart = async () => {
    if (!user || selectedLeadIds.size === 0 || sortedSteps.length === 0) return

    setStarting(true)
    try {
      const firstStep = sortedSteps[0]
      const leadIds = Array.from(selectedLeadIds)

      // 0. Save scheduled_time, AI prompt overrides, and send_note to all step config_json
      for (const step of sortedSteps) {
        const existingConfig = (step.config_json || {}) as Record<string, unknown>
        const updates: Record<string, unknown> = { ...existingConfig }
        let changed = false

        // Scheduled time
        const time = stepScheduledTimes[step.id]
        if (time) { updates.scheduled_time = time; changed = true }

        // AI prompt overrides
        const msgId = stepMessageOverrides[step.id]
        if (msgId) { updates.ai_prompt_id = msgId === 'none' ? null : msgId; changed = true }

        const resId = stepResearchOverrides[step.id]
        if (resId) { updates.ai_research_prompt_id = resId === 'none' ? null : resId; changed = true }

        const exId = stepExampleOverrides[step.id]
        if (exId) { updates.ai_example_section_id = exId === 'none' ? null : exId; changed = true }

        const tplId = stepTemplateOverrides[step.id]
        if (tplId) {
          updates.template_id = tplId === 'none' ? null : tplId
          // If a template is selected, also save the template body as message_template
          if (tplId !== 'none') {
            const tpl = templates.find((t) => t.id === tplId)
            if (tpl) updates.message_template = tpl.body_template
          }
          changed = true
        }

        // Send note flag for LinkedIn Connect steps
        if (stepSendNote[step.id] !== undefined) {
          updates.send_note = stepSendNote[step.id]
          changed = true
        }

        // Signals toggle. CA-034(b): still written on every submit, but the
        // toggle is now hydrated from saved config (seedUseSignals), so this
        // persists the user's true choice instead of clobbering a saved false
        // with the old hardcoded default of true.
        updates.use_signals = useSignals
        changed = true

        if (changed) {
          const { error: cfgError } = await supabase
            .from('cadence_steps')
            .update({ config_json: updates })
            .eq('id', step.id)
          if (cfgError) throw cfgError
        }
      }

      // 1. Classify selected leads: fresh vs mid-flight (BUG-084).
      // Re-running the dialog must NOT reset leads that already progressed:
      // a mid-flight lead keeps its cadence_leads row (current_step_id intact)
      // and is re-scheduled FROM ITS CURRENT STEP, not from step 1.
      const FETCH_CHUNK = 100
      const membershipByLead = new Map<string, { current_step_id: string | null; status: string }>()
      const leadsWithSentInstances = new Set<string>()
      for (let i = 0; i < leadIds.length; i += FETCH_CHUNK) {
        const chunk = leadIds.slice(i, i + FETCH_CHUNK)
        const { data: memberships, error: memErr } = await supabase
          .from('cadence_leads')
          .select('lead_id, current_step_id, status')
          .eq('cadence_id', cadence.id)
          .in('lead_id', chunk)
        if (memErr) throw memErr
        for (const m of memberships || []) {
          membershipByLead.set(m.lead_id, {
            current_step_id: m.current_step_id ?? null,
            status: m.status,
          })
        }
        const { data: sentInstances, error: sentErr } = await supabase
          .from('lead_step_instances')
          .select('lead_id')
          .eq('cadence_id', cadence.id)
          .eq('status', 'sent')
          .in('lead_id', chunk)
        if (sentErr) throw sentErr
        for (const si of sentInstances || []) leadsWithSentInstances.add(si.lead_id)
      }

      const isMidFlight = (leadId: string): boolean => {
        const membership = membershipByLead.get(leadId)
        if (!membership) return false
        const beyondFirstStep =
          !!membership.current_step_id && membership.current_step_id !== firstStep.id
        return beyondFirstStep || leadsWithSentInstances.has(leadId)
      }
      const midFlightSet = new Set(leadIds.filter((leadId) => isMidFlight(leadId)))
      const freshLeadIds = leadIds.filter((leadId) => !midFlightSet.has(leadId))

      // Fresh leads (never progressed past step 1, nothing sent) start from step 1
      const cadenceLeadInserts = freshLeadIds.map((leadId) => ({
        cadence_id: cadence.id,
        lead_id: leadId,
        owner_id: user.id,
        org_id: orgId!,
        current_step_id: firstStep.id,
        status: 'scheduled',
      }))

      // Chunk cadence_leads upsert for large lead lists
      const UPSERT_CHUNK = 500
      for (let i = 0; i < cadenceLeadInserts.length; i += UPSERT_CHUNK) {
        const chunk = cadenceLeadInserts.slice(i, i + UPSERT_CHUNK)
        const { error: clError } = await supabase.from('cadence_leads').upsert(chunk, {
          onConflict: 'cadence_id,lead_id',
        })
        if (clError) throw clError
      }

      // Mid-flight leads keep their row untouched UNLESS their status is
      // 'pending'/'generated': process-queue's membership gate only executes
      // schedules for 'active'/'scheduled' leads, so those get a status-only
      // flip (current_step_id preserved). Any other status (e.g. 'paused') is
      // never touched — pausing protections stay armed.
      const midFlightToActivate = Array.from(midFlightSet).filter((leadId) => {
        const status = membershipByLead.get(leadId)?.status
        return status === 'pending' || status === 'generated'
      })
      for (let i = 0; i < midFlightToActivate.length; i += FETCH_CHUNK) {
        const chunk = midFlightToActivate.slice(i, i + FETCH_CHUNK)
        const { error: actError } = await supabase
          .from('cadence_leads')
          .update({ status: 'scheduled', updated_at: new Date().toISOString() })
          .eq('cadence_id', cadence.id)
          .in('lead_id', chunk)
        if (actError) throw actError
      }

      // 2. Create lead_step_instances for ALL steps (not just first).
      // ignoreDuplicates: existing instances are NEVER overwritten — a 'sent'
      // instance must never flip back to 'pending', or the process-queue dedup
      // (BUG-065) would be disarmed and already-contacted leads re-messaged.
      const lsiInserts = sortedSteps.flatMap((step) =>
        leadIds.map((leadId) => ({
          cadence_id: cadence.id,
          cadence_step_id: step.id,
          lead_id: leadId,
          owner_id: user.id,
          org_id: orgId!,
          status: 'pending',
        }))
      )

      // Chunk lead_step_instances upsert (531 leads × 8 steps = 4248 rows)
      for (let i = 0; i < lsiInserts.length; i += UPSERT_CHUNK) {
        const chunk = lsiInserts.slice(i, i + UPSERT_CHUNK)
        const { error: lsiError } = await supabase.from('lead_step_instances').upsert(chunk, {
          onConflict: 'cadence_step_id,lead_id',
          ignoreDuplicates: true,
        })
        if (lsiError) throw lsiError
      }

      // 3. Create schedules for ALL steps at their configured dates + times
      const now = new Date()

      // Helper: convert explicit date (YYYY-MM-DD) + time (HH:MM) in a timezone to UTC
      const toUTCFromDateStr = (dateStr: string, timeStr: string, tz: string): Date => {
        const [year, month, day] = dateStr.split('-').map(Number)
        const [hours, minutes] = timeStr.split(':').map(Number)
        const guessUTC = Date.UTC(year, month - 1, day, hours, minutes, 0)
        const guess = new Date(guessUTC)
        const timeFmt = new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, hourCycle: 'h23' })
        const parts = timeFmt.formatToParts(guess)
        const lH = parseInt(parts.find((p) => p.type === 'hour')?.value || '0')
        const lM = parseInt(parts.find((p) => p.type === 'minute')?.value || '0')
        const lD = parseInt(parts.find((p) => p.type === 'day')?.value || '0')
        const lMo = parseInt(parts.find((p) => p.type === 'month')?.value || '0')
        const lY = parseInt(parts.find((p) => p.type === 'year')?.value || '0')
        let diffMin = (hours * 60 + minutes) - (lH * 60 + lM)
        // Compare full dates chronologically — comparing day-of-month alone inverts
        // the sign across month boundaries (e.g. target day 1 vs local day 28/30/31).
        const targetDayUTC = Date.UTC(year, month - 1, day)
        const localDayUTC = Date.UTC(lY, lMo - 1, lD)
        if (localDayUTC !== targetDayUTC) diffMin += (targetDayUTC > localDayUTC ? 1 : -1) * 1440
        return new Date(guessUTC + diffMin * 60 * 1000)
      }

      // Group steps by their scheduled date and compute a uniform shift per date-group:
      // If the first step of a date has already passed, shift ALL steps that day forward
      // by (now + 60s − firstStepTime), preserving the user's configured intervals.
      const dateStepGroups: Record<string, typeof sortedSteps> = {}
      for (const step of sortedSteps) {
        const dateKey = stepScheduledDates[step.id] || getDefaultDate(step.day_offset, timezone, now)
        if (!dateStepGroups[dateKey]) dateStepGroups[dateKey] = []
        dateStepGroups[dateKey].push(step)
      }

      const dateShiftMs: Record<string, number> = {}
      for (const [dateStr, dateSteps] of Object.entries(dateStepGroups)) {
        const firstTime = stepScheduledTimes[dateSteps[0].id] || '09:00'
        const firstStepUTC = toUTCFromDateStr(dateStr, firstTime, timezone)
        dateShiftMs[dateStr] = firstStepUTC <= now
          ? (now.getTime() + 60_000) - firstStepUTC.getTime()
          : 0
      }

      // Mid-flight leads resume from their current step; fresh leads get the
      // full calendar. If a mid-flight lead's current step no longer exists
      // (deleted), fall back to the full calendar — the 'sent' instance dedup
      // in process-queue still prevents re-sends of already-executed steps.
      const stepIndexById = new Map(sortedSteps.map((step, idx) => [step.id, idx]))
      const startIndexByLead = new Map<string, number>(
        leadIds.map((leadId): [string, number] => {
          if (!midFlightSet.has(leadId)) return [leadId, 0]
          const currentStepId = membershipByLead.get(leadId)?.current_step_id
          return [leadId, (currentStepId ? stepIndexById.get(currentStepId) : undefined) ?? 0]
        })
      )

      // BUG-095: a mid-flight lead's CURRENT step may already have a live
      // schedule created by advanceLeadToNextStep / resume at the correct
      // relative date. Re-running the dialog must NOT cancel + re-create it
      // at the dialog's dates (defaults re-base day_offset to TODAY, silently
      // deferring the pending touch by ~day_offset days on every re-run).
      // Map each mid-flight lead to its current step and record which pairs
      // already have a live schedule: those are excluded from the mass cancel
      // below AND skipped on insert (mig 047's partial unique index on live
      // lead+step would otherwise 23505 the whole insert chunk). Later steps
      // of the mid-flight lead DO take the new dialog calendar (BUG-084).
      const currentStepByLead = new Map<string, string>()
      for (const leadId of midFlightSet) {
        const currentStepId = membershipByLead.get(leadId)?.current_step_id
        if (currentStepId && stepIndexById.has(currentStepId)) {
          currentStepByLead.set(leadId, currentStepId)
        }
      }
      const liveCurrentSchedulePairs = new Set<string>()
      const midFlightWithCurrentStep = Array.from(currentStepByLead.keys())
      for (let i = 0; i < midFlightWithCurrentStep.length; i += FETCH_CHUNK) {
        const chunk = midFlightWithCurrentStep.slice(i, i + FETCH_CHUNK)
        const { data: liveSchedules, error: liveErr } = await supabase
          .from('schedules')
          .select('lead_id, cadence_step_id')
          .eq('cadence_id', cadence.id)
          .eq('org_id', orgId!)
          .in('status', ['scheduled', 'processing'])
          .in('lead_id', chunk)
        if (liveErr) throw liveErr
        for (const s of liveSchedules || []) {
          if (currentStepByLead.get(s.lead_id) === s.cadence_step_id) {
            liveCurrentSchedulePairs.add(`${s.lead_id}:${s.cadence_step_id}`)
          }
        }
      }

      const scheduleInserts = sortedSteps.flatMap((step, stepIndex) => {
        const stepTime = stepScheduledTimes[step.id] || '09:00'
        const stepDate = stepScheduledDates[step.id] || getDefaultDate(step.day_offset, timezone, now)
        const stepUTC = toUTCFromDateStr(stepDate, stepTime, timezone)
        const shift = dateShiftMs[stepDate] ?? 0

        return leadIds
          .filter((leadId) => (startIndexByLead.get(leadId) ?? 0) <= stepIndex)
          // BUG-095: don't re-create the live schedule of a mid-flight lead's
          // current step — it survives the cancel below at its original date.
          .filter((leadId) => !liveCurrentSchedulePairs.has(`${leadId}:${step.id}`))
          .map((leadId, index) => ({
            cadence_id: cadence.id,
            cadence_step_id: step.id,
            lead_id: leadId,
            owner_id: user.id,
            org_id: orgId!,
            // Apply date-group shift + 10s per-lead stagger for API rate-limiting safety
            scheduled_at: new Date(stepUTC.getTime() + shift + index * 10_000).toISOString(),
            timezone: 'UTC',
            status: 'scheduled',
          }))
      })

      // Cancel ALL non-terminal schedules for these leads in this cadence (prevent duplicates)
      // Must include 'processing' too — otherwise re-starting automation while process-queue
      // is actively running leaves processing items alive alongside new inserts → duplicate sends.
      // Chunk the cancel by lead IDs to avoid PostgREST URL length limits with large lead lists.
      // BUG-095: mid-flight leads keep the live schedule of their CURRENT step
      // (already at the correct date) — only their OTHER steps are canceled.
      const CANCEL_CHUNK = 100
      const cancelAllLeadIds = leadIds.filter((leadId) => !currentStepByLead.has(leadId))
      for (let i = 0; i < cancelAllLeadIds.length; i += CANCEL_CHUNK) {
        const chunk = cancelAllLeadIds.slice(i, i + CANCEL_CHUNK)
        const { error: cancelError } = await supabase
          .from('schedules')
          .update({ status: 'canceled', last_error: 'Replaced by new automation run', updated_at: new Date().toISOString() })
          .eq('cadence_id', cadence.id)
          .eq('org_id', orgId!)
          .in('status', ['scheduled', 'processing'])
          .in('lead_id', chunk)
        if (cancelError) throw cancelError
      }
      // Group mid-flight leads by current step so each group's cancel excludes
      // that step via a single .neq() filter (at most steps.length groups).
      const midFlightByCurrentStep = new Map<string, string[]>()
      for (const [leadId, stepId] of currentStepByLead) {
        const group = midFlightByCurrentStep.get(stepId)
        if (group) group.push(leadId)
        else midFlightByCurrentStep.set(stepId, [leadId])
      }
      for (const [stepId, groupLeadIds] of midFlightByCurrentStep) {
        for (let i = 0; i < groupLeadIds.length; i += CANCEL_CHUNK) {
          const chunk = groupLeadIds.slice(i, i + CANCEL_CHUNK)
          const { error: cancelError } = await supabase
            .from('schedules')
            .update({ status: 'canceled', last_error: 'Replaced by new automation run', updated_at: new Date().toISOString() })
            .eq('cadence_id', cadence.id)
            .eq('org_id', orgId!)
            .in('status', ['scheduled', 'processing'])
            .neq('cadence_step_id', stepId)
            .in('lead_id', chunk)
          if (cancelError) throw cancelError
        }
      }

      // Insert schedules in chunks to handle large cadences (e.g. 500+ leads × 8 steps = 4000+ rows)
      const INSERT_CHUNK = 500
      for (let i = 0; i < scheduleInserts.length; i += INSERT_CHUNK) {
        const chunk = scheduleInserts.slice(i, i + INSERT_CHUNK)
        const { error: schedError } = await supabase.from('schedules').insert(chunk)
        if (schedError) throw schedError
      }

      // 4. Update cadence to automated + active with timezone
      const { error: cadError } = await supabase
        .from('cadences')
        .update({
          automation_mode: 'automated',
          status: 'active',
          timezone: timezone,
          updated_at: new Date().toISOString(),
        })
        .eq('id', cadence.id)
      if (cadError) throw cadError

      // 5. Create notification
      await supabase.from('notifications').insert({
        owner_id: user.id,
        org_id: orgId!,
        cadence_id: cadence.id,
        type: 'automation_started',
        title: `Automatizacion iniciada: ${cadence.name}`,
        body: `Se inicio la automatizacion para ${leadIds.length} leads con ${steps.length} steps.`,
        channel: 'system',
      })

      // 6. Log activity
      await supabase.from('activity_log').insert({
        owner_id: user.id,
        org_id: orgId!,
        cadence_id: cadence.id,
        action: 'automation_started',
        status: 'ok',
        details: {
          lead_count: leadIds.length,
          step_count: steps.length,
          first_step_id: firstStep.id,
        },
      })

      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['cadences'] })
      queryClient.invalidateQueries({ queryKey: ['cadence-leads-direct'] })

      toast.success(`Automatizacion iniciada para ${leadIds.length} leads`)
      onOpenChange(false)
      setSelectedLeadIds(new Set())
      setSearchQuery('')
      setStepMessageOverrides({})
      setStepResearchOverrides({})
      setStepExampleOverrides({})
      setStepTemplateOverrides({})
      // Re-seed from the just-persisted config instead of blanking to the old
      // hardcoded defaults, so if the dialog is reopened before the effect
      // re-runs it still shows the user's saved send_note / use_signals choices.
      setStepSendNote(seedSendNote(steps))
      setUseSignals(seedUseSignals(steps))
      setStepScheduledTimes({})
      setStepScheduledDates({})
    } catch (error) {
      console.error('Error starting automation:', error)
      toast.error(error instanceof Error ? error.message : 'Error al iniciar la automatizacion')
    } finally {
      setStarting(false)
    }
  }

  // CA-054: return an automated cadence to manual mode. Flipping automation_mode
  // to 'manual' stops process-queue's advanceLeadToNextStep from auto-scheduling
  // the next touch (it reads cadences.automation_mode), and cancelling the
  // pre-created pending schedules stops the already-queued future touches from
  // firing. This does NOT touch the org outreach engine — it only changes this
  // cadence's flags, closing the approve/pause/adjust loop from the UI (reverting
  // used to require a DB UPDATE).
  const handleRevertToManual = async () => {
    if (!orgId) return
    if (
      !confirm(
        `¿Volver la cadencia "${cadence.name}" a modo MANUAL?\n\n` +
          'Se detiene la programación automática de los siguientes pasos y se ' +
          'cancelan los envíos pendientes ya en cola. Los leads permanecen en la ' +
          'cadencia; podrás ejecutar cada paso manualmente o reiniciar la ' +
          'automatización cuando quieras.'
      )
    ) {
      return
    }

    setRevertingToManual(true)
    try {
      // 1. Flip the mode so no NEW schedules are auto-created going forward.
      await updateCadence(cadence.id, { automation_mode: 'manual', status: 'draft' })

      // 2. Cancel the pre-created pending schedules so the queued future touches
      // don't execute if the engine is later turned on. Same cancel shape as
      // removeLeadFromCadence / markStepDoneForLead.
      const { error: cancelError } = await supabase
        .from('schedules')
        .update({
          status: 'canceled',
          last_error: 'Cadence reverted to manual mode',
          updated_at: new Date().toISOString(),
        })
        .eq('cadence_id', cadence.id)
        .eq('org_id', orgId)
        .in('status', ['scheduled', 'processing'])
      if (cancelError) throw cancelError

      queryClient.invalidateQueries({ queryKey: ['cadences'] })
      queryClient.invalidateQueries({ queryKey: ['cadence-schedules'] })
      queryClient.invalidateQueries({ queryKey: ['outreach-scheduled'] })
      queryClient.invalidateQueries({ queryKey: ['cadence-leads-direct'] })

      toast.success('Cadencia en modo manual. Programación automática detenida.')
      onOpenChange(false)
    } catch (error) {
      console.error('Error reverting cadence to manual:', error)
      toast.error(error instanceof Error ? error.message : 'No se pudo volver a modo manual')
    } finally {
      setRevertingToManual(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-violet-600" />
            Iniciar Automatizacion
          </DialogTitle>
          <DialogDescription>
            Selecciona los leads para ejecutar automaticamente la cadencia "{cadence.name}"
          </DialogDescription>
        </DialogHeader>

        {/*
          TODO(UXA): replace this static notice with <EngineStatusBanner /> from
          src/components/outreach/EngineStatusBanner.tsx once it lands. That
          component shows the LIVE org outreach engine state (ON/OFF) read from
          outreach-engine-status. Until then, this honest static banner tells the
          user that starting automation only QUEUES the touches — nothing sends
          until the engine is turned on at /settings/outreach. Kept as a plain
          element so the build never depends on a not-yet-created import.
        */}
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
          <Power className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            El motor de outreach está <strong>apagado</strong> a nivel
            organización. Iniciar la automatización <strong>programa</strong> los
            pasos, pero no se envía nada hasta activarlo desde{' '}
            <span className="font-mono">/settings/outreach</span>.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto space-y-6 py-4">
          {/* Step Summary */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              Steps de la cadencia ({sortedSteps.length})
            </h3>
            <div className="flex items-center gap-3 rounded-lg border p-2.5">
              <Zap className="h-4 w-4 text-amber-500 shrink-0" />
              <label className="text-sm flex-1">Señales de venta</label>
              <span className="text-xs text-muted-foreground mr-2">
                {useSignals ? 'Activadas' : 'Desactivadas'}
              </span>
              <Switch checked={useSignals} onCheckedChange={setUseSignals} />
            </div>
            <div className="flex items-center gap-3 rounded-lg border p-2.5">
              <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
              <label className="text-sm flex-1">Zona horaria</label>
              <Select value={timezone} onValueChange={setTimezone}>
                <SelectTrigger className="w-64 h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  <SelectItem value="Pacific/Honolulu">Hawaii (HST) GMT-10</SelectItem>
                  <SelectItem value="America/Anchorage">Alaska (AKST) GMT-9</SelectItem>
                  <SelectItem value="America/Los_Angeles">Pacific (PST) GMT-8</SelectItem>
                  <SelectItem value="America/Denver">Mountain (MST) GMT-7</SelectItem>
                  <SelectItem value="America/Chicago">Central (CST) GMT-6</SelectItem>
                  <SelectItem value="America/Mexico_City">Mexico (CST) GMT-6</SelectItem>
                  <SelectItem value="America/New_York">Eastern (EST) GMT-5</SelectItem>
                  <SelectItem value="America/Bogota">Colombia (COT) GMT-5</SelectItem>
                  <SelectItem value="America/Lima">Peru (PET) GMT-5</SelectItem>
                  <SelectItem value="America/Caracas">Venezuela (VET) GMT-4</SelectItem>
                  <SelectItem value="America/Santiago">Chile (CLT) GMT-4</SelectItem>
                  <SelectItem value="America/Argentina/Buenos_Aires">Argentina (ART) GMT-3</SelectItem>
                  <SelectItem value="America/Sao_Paulo">Brazil (BRT) GMT-3</SelectItem>
                  <SelectItem value="Atlantic/Azores">Azores (AZOT) GMT-1</SelectItem>
                  <SelectItem value="UTC">UTC GMT+0</SelectItem>
                  <SelectItem value="Europe/London">London (GMT) GMT+0</SelectItem>
                  <SelectItem value="Europe/Madrid">Spain (CET) GMT+1</SelectItem>
                  <SelectItem value="Europe/Paris">France (CET) GMT+1</SelectItem>
                  <SelectItem value="Europe/Berlin">Germany (CET) GMT+1</SelectItem>
                  <SelectItem value="Europe/Bucharest">Eastern Europe (EET) GMT+2</SelectItem>
                  <SelectItem value="Asia/Jerusalem">Israel (IST) GMT+2</SelectItem>
                  <SelectItem value="Africa/Johannesburg">South Africa (SAST) GMT+2</SelectItem>
                  <SelectItem value="Europe/Istanbul">Turkey (TRT) GMT+3</SelectItem>
                  <SelectItem value="Asia/Riyadh">Saudi Arabia (AST) GMT+3</SelectItem>
                  <SelectItem value="Asia/Dubai">Dubai (GST) GMT+4</SelectItem>
                  <SelectItem value="Asia/Karachi">Pakistan (PKT) GMT+5</SelectItem>
                  <SelectItem value="Asia/Kolkata">India (IST) GMT+5:30</SelectItem>
                  <SelectItem value="Asia/Dhaka">Bangladesh (BST) GMT+6</SelectItem>
                  <SelectItem value="Asia/Bangkok">Thailand (ICT) GMT+7</SelectItem>
                  <SelectItem value="Asia/Singapore">Singapore (SGT) GMT+8</SelectItem>
                  <SelectItem value="Asia/Shanghai">China (CST) GMT+8</SelectItem>
                  <SelectItem value="Asia/Tokyo">Japan (JST) GMT+9</SelectItem>
                  <SelectItem value="Asia/Seoul">Korea (KST) GMT+9</SelectItem>
                  <SelectItem value="Australia/Sydney">Australia (AEST) GMT+10</SelectItem>
                  <SelectItem value="Pacific/Auckland">New Zealand (NZST) GMT+12</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              {sortedSteps.map((step) => {
                const config = step.config_json as Record<string, unknown>
                // Optional chain: DB allows step_types outside the StepType union
                // (e.g. generate_ss_deck) with no STEP_TYPE_CONFIG entry.
                const needsContent = !!STEP_TYPE_CONFIG[step.step_type]?.hasTextBox
                const isConnect = step.step_type === 'linkedin_connect'
                const sendNote = !!stepSendNote[step.id]

                // Steps that don't need any content at all (e.g. LinkedIn Like)
                if (!needsContent && !isConnect) {
                  return (
                    <div
                      key={step.id}
                      className="flex items-center gap-3 rounded-lg border p-2.5 text-sm"
                    >
                      <Badge variant="outline" className="text-xs shrink-0">
                        Dia {step.day_offset}
                      </Badge>
                      <span className="flex-1 truncate">{step.step_label}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <Input
                          type="date"
                          value={stepScheduledDates[step.id] || getDefaultDate(step.day_offset, timezone, new Date())}
                          onChange={(e) => setStepScheduledDates((prev) => ({ ...prev, [step.id]: e.target.value }))}
                          className="w-36 h-7 text-xs"
                        />
                        <Input
                          type="time"
                          value={stepScheduledTimes[step.id] || '09:00'}
                          onChange={(e) => setStepScheduledTimes((prev) => ({ ...prev, [step.id]: e.target.value }))}
                          className="w-28 h-7 text-xs"
                        />
                        <Badge variant="secondary" className="text-xs shrink-0">
                          <CheckCircle className="mr-1 h-3 w-3" />
                          OK
                        </Badge>
                      </div>
                    </div>
                  )
                }

                // For LinkedIn Connect: show toggle for sending note
                // For other content steps: always show selectors
                const showSelectors = isConnect ? sendNote : true

                // Filter message prompts compatible with this step type
                const compatibleMessagePrompts = aiPrompts.filter(
                  (p) => p.prompt_type === 'message' && (p.step_type === step.step_type || p.step_type === null)
                )

                // Current values (override → existing config → none)
                const currentMessageId = stepMessageOverrides[step.id] || (config?.ai_prompt_id as string) || 'none'
                const currentResearchId = stepResearchOverrides[step.id] || (config?.ai_research_prompt_id as string) || 'none'
                const currentExampleId = stepExampleOverrides[step.id] || (config?.ai_example_section_id as string) || 'none'
                const currentTemplateId = stepTemplateOverrides[step.id] || (config?.template_id as string) || 'none'

                return (
                  <div key={step.id} className="rounded-lg border p-3 space-y-2">
                    <div className="flex items-center gap-3 text-sm">
                      <Badge variant="outline" className="text-xs shrink-0">
                        Dia {step.day_offset}
                      </Badge>
                      <span className="font-medium flex-1 truncate">{step.step_label}</span>
                      <div className="flex items-center gap-2 shrink-0">
                        <Input
                          type="date"
                          value={stepScheduledDates[step.id] || getDefaultDate(step.day_offset, timezone, new Date())}
                          onChange={(e) => setStepScheduledDates((prev) => ({ ...prev, [step.id]: e.target.value }))}
                          className="w-36 h-7 text-xs"
                        />
                        <Input
                          type="time"
                          value={stepScheduledTimes[step.id] || '09:00'}
                          onChange={(e) => setStepScheduledTimes((prev) => ({ ...prev, [step.id]: e.target.value }))}
                          className="w-28 h-7 text-xs"
                        />
                        {isConnect && (
                          <label className="flex items-center gap-1.5 cursor-pointer text-xs text-muted-foreground whitespace-nowrap">
                            <Checkbox
                              checked={sendNote}
                              onCheckedChange={(checked) =>
                                setStepSendNote((prev) => ({ ...prev, [step.id]: !!checked }))
                              }
                            />
                            Enviar nota con AI
                          </label>
                        )}
                      </div>
                    </div>
                    {showSelectors && (
                      <div className="grid grid-cols-4 gap-2">
                        {/* Message Prompt */}
                        <div className="space-y-1">
                          <label className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <Brain className="h-3 w-3" /> Prompt de Mensaje
                          </label>
                          <Select
                            value={currentMessageId}
                            onValueChange={(value) =>
                              setStepMessageOverrides((prev) => ({ ...prev, [step.id]: value }))
                            }
                          >
                            <SelectTrigger
                              className={`h-7 text-xs ${
                                currentMessageId === 'none'
                                  ? 'border-destructive/50 text-destructive'
                                  : 'border-violet-300 text-violet-600'
                              }`}
                            >
                              <SelectValue placeholder="Sin prompt" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sin prompt</SelectItem>
                              {compatibleMessagePrompts.map((prompt) => (
                                <SelectItem key={prompt.id} value={prompt.id}>
                                  {prompt.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Research Prompt */}
                        <div className="space-y-1">
                          <label className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <FlaskConical className="h-3 w-3" /> Prompt de Investigacion
                          </label>
                          <Select
                            value={currentResearchId}
                            onValueChange={(value) =>
                              setStepResearchOverrides((prev) => ({ ...prev, [step.id]: value }))
                            }
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Ninguno" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Ninguno</SelectItem>
                              {researchPrompts.map((prompt) => (
                                <SelectItem key={prompt.id} value={prompt.id}>
                                  {prompt.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Example Section */}
                        <div className="space-y-1">
                          <label className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <BookOpen className="h-3 w-3" /> Mensajes Base
                          </label>
                          <Select
                            value={currentExampleId}
                            onValueChange={(value) =>
                              setStepExampleOverrides((prev) => ({ ...prev, [step.id]: value }))
                            }
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Ninguno" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Ninguno</SelectItem>
                              {exampleSections.map((section) => (
                                <SelectItem key={section.id} value={section.id}>
                                  {section.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Template */}
                        <div className="space-y-1">
                          <label className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <FileText className="h-3 w-3" /> Template
                          </label>
                          <Select
                            value={currentTemplateId}
                            onValueChange={(value) =>
                              setStepTemplateOverrides((prev) => ({ ...prev, [step.id]: value }))
                            }
                          >
                            <SelectTrigger className="h-7 text-xs">
                              <SelectValue placeholder="Ninguno" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Ninguno</SelectItem>
                              {templates.map((tpl) => (
                                <SelectItem key={tpl.id} value={tpl.id}>
                                  {tpl.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            {stepsWithoutAI.length > 0 && (
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                {stepsWithoutAI.length} step(s) sin AI prompt ni template. Los mensajes se enviaran vacios o fallaran.
              </p>
            )}
          </div>

          {/* Lead Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Seleccionar Leads ({selectedLeadIds.size} de {availableLeads.length})
              </h3>
              <Button variant="ghost" size="sm" onClick={toggleAll}>
                {selectedLeadIds.size === filteredLeads.length ? 'Deseleccionar todos' : 'Seleccionar todos'}
              </Button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar leads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {availableLeads.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No hay leads disponibles. Todos los leads ya estan en esta cadencia.
              </p>
            ) : (
              <div className="max-h-[300px] overflow-y-auto space-y-1 rounded-lg border p-2">
                {filteredLeads.map((lead) => (
                  <label
                    key={lead.id}
                    className="flex items-center gap-3 rounded-md p-2 hover:bg-muted/50 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedLeadIds.has(lead.id)}
                      onCheckedChange={() => toggleLead(lead.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {lead.first_name} {lead.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {lead.company}
                        {lead.title && ` - ${lead.title}`}
                      </p>
                    </div>
                    {lead.email && (
                      <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                        {lead.email}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          {/* CA-054: only shown for cadences currently in automated mode — the
              way back out of automation, without a DB edit. */}
          {cadence.automation_mode === 'automated' ? (
            <Button
              variant="outline"
              onClick={handleRevertToManual}
              disabled={revertingToManual || starting}
              className="text-amber-700 hover:text-amber-800"
            >
              {revertingToManual ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Cambiando...
                </>
              ) : (
                <>
                  <Hand className="mr-2 h-4 w-4" />
                  Volver a modo manual
                </>
              )}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleStart}
              disabled={starting || revertingToManual || selectedLeadIds.size === 0}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white"
            >
              {starting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Iniciando...
                </>
              ) : (
                <>
                  <Zap className="mr-2 h-4 w-4" />
                  Iniciar ({selectedLeadIds.size} leads)
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
